import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Sparkles, Leaf, Heart, Flame, Instagram, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [instagram, setInstagram] = useState([]);
  const [covers, setCovers] = useState({});
  const [testimonials, setTestimonials] = useState([]);
  const [homepageTabs, setHomepageTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products?featured=true").then(({ data }) => {
      setFeatured(data);
      setLoading(false);
    }).catch(() => setLoading(false));
    api.get("/instagram").then(({ data }) => setInstagram(data)).catch(() => {});
    api.get("/category-covers").then(({ data }) => setCovers(data)).catch(() => {});
    api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => {});
    api.get("/homepage-tabs").then(({ data }) => {
      setHomepageTabs(data);
      if (data.length > 0) setActiveTabId(data[0].id);
    }).catch(() => {});
  }, []);

  const cover = (slug, fallback) => covers[slug] || fallback;

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-24">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7 space-y-8">
              <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] animate-fade-up">
                ✦ Small-batch · Howrah, West Bengal · 5.0 ★
              </div>
              <h1 className="font-serif-display text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight animate-fade-up-delay-1">
                Something for
                <br /><em className="text-[#D27D60]">Everyone.</em>
              </h1>
              <p className="text-base md:text-lg text-[#4a4a4a] max-w-lg leading-relaxed animate-fade-up-delay-2">
                Thoughtful gifts, delightful surprises, and little treasures for every age and every occasion — hand-poured and hand-finished in our Howrah studio.
              </p>
              <div className="flex flex-wrap items-center gap-4 animate-fade-up-delay-3">
                <Link
                  to="/shop"
                  className="group bg-[#1A1A1A] text-[#FDFBF7] px-7 py-4 rounded-full inline-flex items-center gap-3 text-sm uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors"
                  data-testid="hero-shop-btn"
                >
                  Shop the collection
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                </Link>
                <Link
                  to="/about"
                  className="text-sm uppercase tracking-widest-plus text-[#1A1A1A] underline underline-offset-8 hover:text-[#D27D60] transition-colors decoration-[#D27D60]/40"
                  data-testid="hero-story-btn"
                >
                  Read our story
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 relative animate-fade-up-delay-4">
              <div className="relative aspect-[4/5] overflow-hidden rounded-lg bg-[#F3EFEA] hover-lift-img">
                <img
                  src="https://customer-assets-jt897jd0.emergentagent.net/job_scent-studio-94/artifacts/lroeyon6_ChatGPT%20Image%20Jul%203%2C%202026%2C%2012_38_40%20PM.png"
                  alt="Garden blooms candle bouquet"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 w-40 h-52 rounded-lg overflow-hidden border-4 border-[#FDFBF7] shadow-[0_20px_60px_rgba(0,0,0,0.15)] hidden md:block hover-lift-img">
                <img
                  src="https://customer-assets-jt897jd0.emergentagent.net/job_scent-studio-94/artifacts/ojnjlsjy_ChatGPT%20Image%20Jul%2017%2C%202026%2C%2010_51_44%20AM.png"
                  alt="Rose teddy heart candle"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -top-6 -right-4 bg-[#FDFBF7] rounded-lg p-4 shadow-[0_20px_60px_rgba(0,0,0,0.08)] hidden md:block">
                <div className="flex gap-1 mb-1">
                  {[0,1,2,3,4].map(i => <Star key={i} className="w-3.5 h-3.5 fill-[#D27D60] text-[#D27D60]" />)}
                </div>
                <div className="text-xs text-[#4a4a4a] font-medium">5.0 on Google</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="border-y border-[#E3DCD2] bg-[#F3EFEA]/40 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: Flame, label: "Hand-poured", sub: "Small batches, big care" },
            { icon: Leaf, label: "Clean burn", sub: "Soy & palm wax" },
            { icon: Heart, label: "Made with love", sub: "In Howrah, WB" },
            { icon: Sparkles, label: "Gift-ready", sub: "Ships wrapped" },
          ].map((v, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FDFBF7] border border-[#E3DCD2] flex items-center justify-center shrink-0">
                <v.icon className="w-4 h-4 text-[#D27D60]" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-sm font-medium">{v.label}</div>
                <div className="text-xs text-[#9BA595]">{v.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories bento */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12 gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3">✦ Explore</div>
            <h2 className="font-serif-display text-5xl md:text-6xl leading-tight">Shop by category</h2>
          </div>
          <Link to="/shop" className="hidden md:inline text-sm uppercase tracking-widest-plus underline underline-offset-8 hover:text-[#D27D60] transition-colors" data-testid="categories-view-all">
            View all
          </Link>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <CategoryTile to="/shop/candles" title="Scented Candles" span="col-span-12 md:col-span-8 aspect-[16/9] md:aspect-[16/10]"
            image={cover("candles", "https://images.unsplash.com/photo-1613068431228-8cb6a1e92573?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxoYW5kbWFkZSUyMGNhbmRsZXMlMjB3YXJtJTIwYWVzdGhldGljfGVufDB8fHx8MTc4MjQ5NDQ5Mnww&ixlib=rb-4.1.0&q=85")}
            large />
          <CategoryTile to="/shop/floral-bouquets" title="Floral Bouquets" span="col-span-6 md:col-span-4 aspect-square"
            image={cover("floral-bouquets", "https://images.pexels.com/photos/19782063/pexels-photo-19782063.jpeg")} />
          <CategoryTile to="/shop/figurines" title="Figurines" span="col-span-6 md:col-span-4 aspect-square"
            image={cover("figurines", "https://images.pexels.com/photos/27549292/pexels-photo-27549292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940")} />
          <CategoryTile to="/shop/diy-kits" title="DIY Kits" span="col-span-6 md:col-span-4 aspect-square"
            image={cover("diy-kits", "https://images.pexels.com/photos/7233953/pexels-photo-7233953.jpeg")} />
          <CategoryTile to="/shop/garden-decor-accessories" title="Garden Décor" span="col-span-12 md:col-span-4 aspect-[16/9] md:aspect-square"
            image={cover("garden-decor-accessories", "https://images.pexels.com/photos/29495742/pexels-photo-29495742.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940")} />
          <CategoryTile to="/shop/little-joys-kids" title="Little Joys for Kids" span="col-span-12 aspect-[16/7] md:aspect-[16/5]"
            image={cover("little-joys-kids", "https://images.pexels.com/photos/8535172/pexels-photo-8535172.jpeg")}
            large />
        </div>
      </section>

      {/* Featured products */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="flex items-end justify-between mb-12 gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3">✦ Bestsellers</div>
            <h2 className="font-serif-display text-5xl md:text-6xl leading-tight">Loved by many</h2>
          </div>
          <Link to="/shop" className="hidden md:inline text-sm uppercase tracking-widest-plus underline underline-offset-8 hover:text-[#D27D60] transition-colors" data-testid="featured-view-all">
            Shop all
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[0,1,2,3].map(i => <div key={i} className="animate-pulse aspect-[4/5] bg-[#F3EFEA] rounded-md" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
            {featured.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </section>

      {/* Dynamic homepage tabs — managed via Admin */}
      {homepageTabs.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="home-tabs-section">
          <div className="mb-10">
            <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3">✦ Featured stories</div>
            <h2 className="font-serif-display text-5xl md:text-6xl leading-tight">Discover more</h2>
          </div>

          <div className="flex flex-wrap gap-2 border-b border-[#E3DCD2] mb-10 overflow-x-auto" data-testid="home-tabs-nav">
            {homepageTabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTabId(t.id)}
                className={`px-5 py-3 text-xs uppercase tracking-widest-plus transition-colors border-b-2 whitespace-nowrap ${
                  activeTabId === t.id
                    ? "border-[#D27D60] text-[#D27D60]"
                    : "border-transparent text-[#1A1A1A] hover:text-[#D27D60]"
                }`}
                data-testid={`home-tab-btn-${t.id}`}
              >
                {t.title}
              </button>
            ))}
          </div>

          {homepageTabs
            .filter((t) => t.id === activeTabId)
            .map((t) => (
              <div
                key={t.id}
                className="grid md:grid-cols-2 gap-10 md:gap-16 items-center"
                data-testid={`home-tab-panel-${t.id}`}
              >
                {t.image_url && (
                  <div className="relative aspect-[4/5] md:aspect-square overflow-hidden rounded-lg bg-[#F3EFEA] hover-lift-img">
                    <img src={t.image_url} alt={t.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className={t.image_url ? "" : "md:col-span-2 max-w-3xl"}>
                  {t.subtitle && (
                    <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3">
                      ✦ {t.subtitle}
                    </div>
                  )}
                  <h3 className="font-serif-display text-4xl md:text-5xl leading-tight mb-5">
                    {t.title}
                  </h3>
                  {t.body && (
                    <p className="text-base text-[#4a4a4a] leading-relaxed whitespace-pre-line mb-6">
                      {t.body}
                    </p>
                  )}
                  {t.link_url && t.link_label && (
                    <Link
                      to={t.link_url}
                      className="group inline-flex items-center gap-3 text-sm uppercase tracking-widest-plus underline underline-offset-8 hover:text-[#D27D60] transition-colors decoration-[#D27D60]/40"
                      data-testid={`home-tab-cta-${t.id}`}
                    >
                      {t.link_label}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                    </Link>
                  )}
                </div>
              </div>
            ))}
        </section>
      )}

      {/* Build-a-bouquet teaser */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <Link
          to="/build-bouquet"
          className="group relative block overflow-hidden rounded-lg bg-[#1A1A1A] text-[#FDFBF7] p-10 md:p-16 hover-lift-img"
          data-testid="home-bouquet-teaser"
        >
          <div className="relative z-10 max-w-2xl">
            <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3">✦ New — build your own</div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[0.95] mb-4">
              Design a bouquet
              <br /><em className="text-[#D27D60]">just for them.</em>
            </h2>
            <p className="text-sm md:text-base text-[#9BA595] max-w-md mb-6">
              Handpick your candles. We hand-wrap them into a ribbon-tied bouquet ready to gift.
            </p>
            <div className="inline-flex items-center gap-2 text-sm uppercase tracking-widest-plus underline underline-offset-8 decoration-[#D27D60]/40 group-hover:decoration-[#D27D60]">
              Start building
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
            </div>
          </div>
          <div className="absolute -right-8 -top-8 w-64 h-64 md:w-96 md:h-96 rounded-full bg-[#D27D60]/10 blur-3xl" />
        </Link>
      </section>

      {/* Brand quote */}
      <section className="bg-[#1A1A1A] text-[#FDFBF7] py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-[11px] uppercase tracking-widest-plus text-[#9BA595] mb-6">✦ Our philosophy</div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-tight mb-8">
            "Hand-poured with love, our candles are made to brighten your home and your heart."
          </h2>
          <div className="text-sm uppercase tracking-widest-plus text-[#D27D60]">— Ave Luxe · Bespoke gifting</div>
        </div>
      </section>

      {/* Reviews */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center mb-12">
          <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3">✦ Reviews</div>
          <h2 className="font-serif-display text-5xl md:text-6xl leading-tight mb-4">Notes from our customers</h2>
          <div className="flex items-center justify-center gap-2">
            {[0,1,2,3,4].map(i => <Star key={i} className="w-5 h-5 fill-[#D27D60] text-[#D27D60]" />)}
            <span className="text-sm ml-2">5.0 on Google · trusted by candle lovers across India</span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6" data-testid="home-testimonials-grid">
          {(testimonials.length > 0 ? testimonials.slice(0, 9) : [
            { id: "f1", name: "Ananya S.", comment: "The scent fills my entire living room without being overpowering. The ceramic vessel is stunning — I use it as a planter now.", location: "Kolkata" },
            { id: "f2", name: "Rohan M.", comment: "Bought the floral candle bouquet as a birthday gift. My sister could not stop talking about the packaging.", location: "Mumbai" },
            { id: "f3", name: "Priya D.", comment: "You can feel this is handmade. Small imperfections that make it feel personal. I've ordered three times.", location: "Bangalore" },
          ]).map((r) => (
            <div key={r.id} className="p-8 bg-[#F3EFEA]/60 rounded-lg border border-[#E3DCD2]" data-testid={`home-testimonial-${r.id}`}>
              <div className="flex gap-1 mb-4">
                {[0,1,2,3,4].map(j => <Star key={j} className="w-3.5 h-3.5 fill-[#D27D60] text-[#D27D60]" />)}
              </div>
              <p className="font-serif-display text-xl leading-snug mb-6">"{r.comment}"</p>
              <div className="text-xs uppercase tracking-widest-plus text-[#9BA595]">
                — {r.name}{r.location ? `, ${r.location}` : ""}
                {r.source && <span className="text-[#D27D60]"> · via {r.source}</span>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Instagram gallery */}
      {instagram.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="home-instagram-section">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-3 flex items-center gap-2">
                <Instagram className="w-3.5 h-3.5" strokeWidth={1.5} /> ✦ From our studio
              </div>
              <h2 className="font-serif-display text-5xl md:text-6xl leading-tight">Follow along</h2>
              <p className="text-sm text-[#4a4a4a] mt-2">Behind the pours, packs and blooms — every day.</p>
            </div>
            <a
              href="https://instagram.com/ave_luxe_candles"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-widest-plus underline underline-offset-8 hover:text-[#D27D60] transition-colors decoration-[#D27D60]/40 self-start"
              data-testid="home-instagram-follow"
            >
              @ave_luxe_candles
              <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {instagram.slice(0, 6).map((p) => (
              <a
                key={p.id}
                href={p.post_url || "https://instagram.com/ave_luxe_candles"}
                target="_blank"
                rel="noreferrer"
                className="group relative aspect-square overflow-hidden rounded-md bg-[#F3EFEA] hover-lift-img"
                data-testid={`home-instagram-post-${p.id}`}
                title={p.caption || "View on Instagram"}
              >
                <img src={p.image_url} alt={p.caption || "Instagram post"} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <Instagram className="w-6 h-6 text-[#FDFBF7] opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function CategoryTile({ to, title, image, span, large }) {
  return (
    <Link
      to={to}
      className={`${span} group relative overflow-hidden rounded-lg block hover-lift-img`}
      data-testid={`category-tile-${to.split("/").pop()}`}
    >
      <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
      <div className="absolute bottom-0 left-0 p-5 md:p-7 text-[#FDFBF7]">
        <div className={`font-serif-display leading-none ${large ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"}`}>{title}</div>
        <div className="text-[10px] uppercase tracking-widest-plus mt-2 flex items-center gap-2 opacity-90">
          Shop now <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
        </div>
      </div>
    </Link>
  );
}
